import { useState, useCallback, useEffect, useRef } from 'react';
import { circlesAPI } from '../services/api';

// Which circle the user last had open, so a reload / PWA-resume keeps you in
// circle mode. Deliberately NOT in the URL (keeps it orthogonal to the view
// stack + the /circle/:token deep link).
const ACTIVE_CIRCLE_KEY = 'sophia_active_circle';

/**
 * Owns the user's circles list + which circle (if any) is currently "active"
 * (the main chat is showing that circle instead of the personal chat).
 * `activeCircleId === null` means personal / single mode.
 *
 * @param {boolean} enabled - only fetch once the user is authenticated
 */
export function useCircles(enabled) {
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCircleId, setActiveCircleId] = useState(() => {
    const saved = sessionStorage.getItem(ACTIVE_CIRCLE_KEY);
    return saved ? Number(saved) : null;
  });
  const [activeCircle, setActiveCircle] = useState(null);
  const loadedRef = useRef(false);
  const detailReqRef = useRef(0); // guards against a stale detail fetch clobbering a newer one

  const refreshCircles = useCallback(async () => {
    if (!enabled) return [];
    setLoading(true);
    try {
      const data = await circlesAPI.getMyCircles();
      const list = data.circles || [];
      setCircles(list);
      return list;
    } catch (e) {
      setCircles([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  // Load the list once the user is authenticated.
  useEffect(() => {
    if (!enabled || loadedRef.current) return;
    loadedRef.current = true;
    refreshCircles();
  }, [enabled, refreshCircles]);

  // Signed out, or the feature switched off (ENABLE_CIRCLES=false): leave circle
  // mode. Without this, the persisted active circle would keep the main chat in
  // a group it can no longer load.
  useEffect(() => {
    if (enabled) return;
    setActiveCircleId(null);
    setActiveCircle(null);
    setCircles([]);
  }, [enabled]);

  // Keep unread badges reasonably fresh while the user actually has circles.
  useEffect(() => {
    if (!enabled || circles.length === 0) return undefined;
    const timer = setInterval(() => { refreshCircles(); }, 45000);
    return () => clearInterval(timer);
  }, [enabled, circles.length, refreshCircles]);

  // Persist the active circle across reloads.
  useEffect(() => {
    if (activeCircleId) sessionStorage.setItem(ACTIVE_CIRCLE_KEY, String(activeCircleId));
    else sessionStorage.removeItem(ACTIVE_CIRCLE_KEY);
  }, [activeCircleId]);

  // Load the active circle's detail (name / objective / members / plan tiers).
  // `clear` blanks the stale detail up-front (on a SWITCH) so the band shows a
  // loading state instead of the previous circle; a plain refresh keeps it.
  const loadActiveDetail = useCallback(async (id, clear = false) => {
    if (!id) { setActiveCircle(null); return null; }
    const seq = ++detailReqRef.current;
    if (clear) setActiveCircle(null);
    try {
      const detail = await circlesAPI.getCircle(id);
      if (seq === detailReqRef.current) setActiveCircle(detail);
      return detail;
    } catch (e) {
      // Circle vanished / no longer a member → fall back to the personal chat.
      if (seq === detailReqRef.current) {
        setActiveCircle(null);
        setActiveCircleId(null);
      }
      return null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    loadActiveDetail(activeCircleId, true); // switch → clear stale detail first
  }, [enabled, activeCircleId, loadActiveDetail]);

  const enterCircle = useCallback((id) => setActiveCircleId(id), []);
  const exitToPersonal = useCallback(() => setActiveCircleId(null), []);

  // Mark a circle read (optimistically clears its badge, then persists).
  const markRead = useCallback(async (circleId, lastMessageId) => {
    if (!circleId) return;
    setCircles(prev => prev.map(c =>
      c.id === circleId ? { ...c, unreadCount: 0, hasUnread: false } : c
    ));
    try { await circlesAPI.markRead(circleId, lastMessageId); } catch (e) { /* best-effort */ }
  }, []);
  const refreshActive = useCallback(
    () => loadActiveDetail(activeCircleId),
    [activeCircleId, loadActiveDetail]
  );

  const createCircle = useCallback(async (name) => {
    const created = await circlesAPI.createCircle(name);
    await refreshCircles();
    if (created?.id) setActiveCircleId(created.id);
    return created;
  }, [refreshCircles]);

  return {
    circles,
    loading,
    activeCircleId,
    activeCircle,
    enterCircle,
    exitToPersonal,
    markRead,
    refreshCircles,
    refreshActive,
    createCircle
  };
}
